import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTicketAlt, FaRegFileAlt, FaFilePdf, FaBan, FaCalendarAlt, FaUser, FaBuilding } from 'react-icons/fa';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const API_URL = process.env.REACT_APP_API_URL;

interface CreditNote {
  id: number;
  credit_note_no: string;
  invoice_id: number;
  invoice_no: string;
  Company_id: number;
  company_name: string;
  Customer_id: number;
  customer_name: string;
  Date: string;
  SGSTAmount: number;
  CGSTAmount: number;
  IGSTAmount: number;
  Total_Amount: number;
  Particulars: string;
  HSN_SAC: string;
  remarks: string;
}

interface Company {
  id: number;
  company_name: string;
  address?: string;
  gst_number?: string;
  state?: string;
  cin?: string;
  email?: string;
  signature?: string;
}

interface Customer {
  id: number;
  company_name: string;
  address?: string;
  city?: string;
  state?: string;
  gst_number?: string;
}

// Amount in words helper (basic, for INR)
function numberToWords(num: number): string {
  if (isNaN(num)) return "";
  if (num === 0) return "Zero";
  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const b = [
    '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'
  ];
  const g = [
    '', 'Thousand', 'Lakh', 'Crore', 'Arab', 'Kharab'
  ];
  let s = '';
  const n = Math.floor(num);
  let numStr = n.toString();
  let digits = numStr.split('').reverse().map(Number);

  function _inWordsHundred(n: number) {
    let str = '';
    if (n > 99) {
      str += a[Math.floor(n / 100)] + ' Hundred ';
      n = n % 100;
    }
    if (n > 19) {
      str += b[Math.floor(n / 10)] + ' ' + a[n % 10];
    } else {
      str += a[n];
    }
    return str.trim();
  }

  let i = 0;
  let words = [];
  let segment;

  let remaining = n;
  if (remaining === 0) return "Zero";
  while (remaining > 0 && i < g.length) {
    if (i === 0) {
      segment = remaining % 1000;
      remaining = Math.floor(remaining / 1000);
    } else {
      segment = remaining % 100;
      remaining = Math.floor(remaining / 100);
    }
    if (segment) words.push(_inWordsHundred(segment) + (g[i] ? ' ' + g[i] : ''));
    i++;
  }

  s = words.reverse().join(' ').replace(/\s+/g, ' ').trim();
  s = s.charAt(0).toUpperCase() + s.slice(1);
  return s;
}

const CreditNotesList: React.FC = () => {
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCN, setSelectedCN] = useState<CreditNote | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [downloading, setDownloading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [cnRes, compRes, custRes] = await Promise.all([
        axios.get(`${API_URL}/auth/credit-notes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/auth/companies`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/auth/customers`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      const activeWorkspaceId = localStorage.getItem("selected_company_id");
      let allCNs = cnRes.data || [];
      let allComps = compRes.data || [];
      if (activeWorkspaceId && activeWorkspaceId !== "all") {
        allCNs = allCNs.filter((cn: any) => String(cn.Company_id) === String(activeWorkspaceId));
        allComps = allComps.filter((c: any) => String(c.id) === String(activeWorkspaceId));
      }
      setCreditNotes(allCNs);
      setCompanies(allComps);
      setCustomers(custRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredCNs = creditNotes.filter(cn => 
    cn.credit_note_no.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cn.invoice_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cn.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCompany = selectedCN ? companies.find(c => c.id === selectedCN.Company_id) : null;
  const activeCustomer = selectedCN ? customers.find(c => c.id === selectedCN.Customer_id) : null;

  // Parse particulars & HSN
  const parsedParticulars = selectedCN ? (() => {
    try {
      const parsed = JSON.parse(selectedCN.Particulars);
      return Array.isArray(parsed) ? parsed : [selectedCN.Particulars];
    } catch {
      return [selectedCN.Particulars];
    }
  })() : [];

  const parsedHsn = selectedCN ? (() => {
    try {
      const parsed = JSON.parse(selectedCN.HSN_SAC);
      return Array.isArray(parsed) ? parsed : [selectedCN.HSN_SAC];
    } catch {
      return [selectedCN.HSN_SAC];
    }
  })() : [];

  const totalTax = selectedCN ? (Number(selectedCN.CGSTAmount) + Number(selectedCN.SGSTAmount) + Number(selectedCN.IGSTAmount)) : 0;
  const taxableValue = selectedCN ? (Number(selectedCN.Total_Amount) - totalTax) : 0;

  const handleDownloadPDF = async () => {
    if (!selectedCN) return;
    setDownloading(true);
    const element = document.getElementById('credit-note-pdf-content');
    if (!element) {
      setDownloading(false);
      return;
    }

    try {
      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 10;
      const imgWidth = 210 - (2 * margin);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
      pdf.save(`CreditNote-${selectedCN.credit_note_no}.pdf`);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
            <span className="p-2 bg-purple-100 text-purple-700 rounded-2xl"><FaTicketAlt /></span>
            Credit Notes
          </h1>
          <p className="text-sm text-slate-500 mt-1">View offset statements and adjustment records generated against cancelled invoices.</p>
        </div>
        <div className="mt-4 md:mt-0 w-full md:w-72">
          <input
            type="text"
            placeholder="Search CN No, Invoice No or Customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-2xl border-2 border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-purple-500 focus:outline-none transition-all"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
        </div>
      ) : filteredCNs.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border-2 border-dashed border-slate-200 shadow-sm">
          <div className="mx-auto w-12 h-12 text-slate-400 mb-3"><FaBan className="w-full h-full" /></div>
          <h3 className="text-lg font-bold text-slate-800">No Credit Notes Found</h3>
          <p className="text-sm text-slate-500 mt-1">No credit notes match the filters or none have been generated yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main List */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-3xl border border-slate-100 shadow-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100 font-sans">
                  <thead className="bg-slate-50/70">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">CN Details</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-slate-400 uppercase tracking-wider">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-slate-400 uppercase tracking-wider">View</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredCNs.map((cn) => (
                      <tr key={cn.id} className={`hover:bg-slate-50/50 transition cursor-pointer ${selectedCN?.id === cn.id ? 'bg-purple-50/30' : ''}`} onClick={() => setSelectedCN(cn)}>
                        <td className="px-4 py-4">
                          <div className="text-sm font-bold text-purple-700 font-mono">{cn.credit_note_no}</div>
                          <div className="text-sm font-bold text-slate-800 mt-0.5">{cn.customer_name}</div>
                          <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1.5">
                            <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-mono font-bold">Inv: {cn.invoice_no || '—'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right text-sm font-bold text-slate-900 whitespace-nowrap">
                          ₹ {Number(cn.Total_Amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedCN(cn); }}
                            className="p-1.5 bg-purple-50 text-purple-600 hover:bg-purple-100 rounded-lg transition"
                            title="Preview Credit Note"
                          >
                            <FaRegFileAlt size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Panel - Beautiful Invoice-like Preview */}
          <div className="lg:col-span-2 space-y-4">
            {selectedCN ? (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-2xl p-6">
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">Credit Note Document</h3>
                    <p className="text-xs text-slate-500">Live preview of generated credit note receipt.</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleDownloadPDF}
                      disabled={downloading}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl text-xs font-bold flex items-center gap-2 hover:from-purple-700 hover:to-indigo-700 shadow transition disabled:opacity-50"
                    >
                      <FaFilePdf /> {downloading ? 'Downloading...' : 'Download PDF'}
                    </button>
                    <button 
                      onClick={() => setSelectedCN(null)}
                      className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition"
                    >
                      Close
                    </button>
                  </div>
                </div>

                {/* Printable Document Box */}
                <div className="border border-slate-200 rounded-2xl overflow-auto p-4 bg-slate-50/50 flex justify-center">
                  <div 
                    id="credit-note-pdf-content" 
                    className="bg-white p-6 shadow-sm border border-slate-100 font-sans" 
                    style={{ width: '794px', minHeight: '1000px', boxSizing: 'border-box' }}
                  >
                    {/* Header */}
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-purple-100 text-purple-700 rounded-xl flex items-center justify-center font-bold text-xl">CN</div>
                        <div>
                          <h2 className="text-xl font-extrabold text-slate-800 uppercase tracking-tight">Credit Note</h2>
                          <div className="text-xs font-bold text-purple-700 font-mono mt-0.5">{selectedCN.credit_note_no}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="inline-block px-3 py-1 bg-blue-600 text-white font-extrabold text-xs rounded-lg uppercase shadow">
                          Credits: ₹{Number(selectedCN.Total_Amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </div>
                        <div className="text-[10px] text-slate-400 font-medium mt-1">Date: {new Date(selectedCN.Date).toLocaleDateString('en-GB')}</div>
                      </div>
                    </div>

                    {/* Company and Invoice Metadata grid */}
                    <div className="grid grid-cols-2 gap-4 border-2 border-slate-100 rounded-xl p-3 mb-4 text-xs">
                      <div>
                        <div className="font-extrabold text-indigo-900 text-sm">{activeCompany?.company_name || selectedCN.company_name}</div>
                        {activeCompany?.address && <div className="text-slate-600 mt-1"><b>Address:</b> {activeCompany.address}</div>}
                        {activeCompany?.gst_number && <div className="text-slate-600 mt-0.5"><b>GSTIN:</b> {activeCompany.gst_number}</div>}
                        {activeCompany?.email && <div className="text-slate-600 mt-0.5"><b>Email:</b> {activeCompany.email}</div>}
                        {activeCompany?.cin && <div className="text-slate-600 mt-0.5"><b>CIN:</b> {activeCompany.cin}</div>}
                      </div>
                      <div className="border-l pl-4 border-slate-100">
                        <div className="font-bold text-slate-700 mb-1">Invoice Reference Details</div>
                        <div className="text-slate-600"><b>Original Invoice:</b> <span className="font-mono font-bold text-slate-800">{selectedCN.invoice_no || '—'}</span></div>
                        <div className="text-slate-600 mt-0.5"><b>Place of Supply:</b> {activeCustomer?.state || '—'}</div>
                        <div className="text-slate-600 mt-0.5"><b>Remarks:</b> {selectedCN.remarks}</div>
                      </div>
                    </div>

                    {/* Customer billing layout */}
                    <div className="grid grid-cols-2 gap-4 border-2 border-slate-100 rounded-xl p-3 mb-6 text-xs">
                      <div>
                        <b className="text-indigo-900 text-[11px] uppercase tracking-wider">Bill To</b>
                        <div className="font-bold text-slate-800 mt-1">{activeCustomer?.company_name || selectedCN.customer_name}</div>
                        {activeCustomer?.address && <div className="text-slate-600 mt-0.5">{activeCustomer.address}</div>}
                        {activeCustomer?.city && <div className="text-slate-600 mt-0.5">{activeCustomer.city}, {activeCustomer.state}</div>}
                        {activeCustomer?.gst_number && <div className="text-slate-600 mt-0.5"><b>GSTIN:</b> {activeCustomer.gst_number}</div>}
                      </div>
                      <div className="border-l pl-4 border-slate-100">
                        <b className="text-indigo-900 text-[11px] uppercase tracking-wider">Ship To</b>
                        <div className="font-bold text-slate-800 mt-1">{activeCustomer?.company_name || selectedCN.customer_name}</div>
                        {activeCustomer?.address && <div className="text-slate-600 mt-0.5">{activeCustomer.address}</div>}
                        {activeCustomer?.city && <div className="text-slate-600 mt-0.5">{activeCustomer.city}, {activeCustomer.state}</div>}
                      </div>
                    </div>

                    {/* Table of offset particulars */}
                    <table className="w-full border border-slate-200 border-collapse rounded-xl overflow-hidden text-xs mb-6">
                      <thead>
                        <tr className="bg-slate-50 text-slate-700 font-extrabold">
                          <th className="border p-2.5 text-center w-12">S.No</th>
                          <th className="border p-2.5 text-left">Item Description / Particulars</th>
                          <th className="border p-2.5 text-center w-24">HSN/SAC</th>
                          <th className="border p-2.5 text-right w-32">Total Value (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedParticulars.map((desc, idx) => (
                          <tr key={idx} className="hover:bg-slate-50/50">
                            <td className="border p-2.5 text-center font-medium text-slate-400">{idx + 1}</td>
                            <td className="border p-2.5 font-medium text-slate-800">{desc}</td>
                            <td className="border p-2.5 text-center font-mono font-medium text-slate-600">{parsedHsn[idx] || selectedCN.HSN_SAC || '—'}</td>
                            <td className="border p-2.5 text-right font-bold text-slate-900">
                              ₹ {Number(selectedCN.Total_Amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Financial details breakdown block */}
                    <div className="flex justify-between items-start text-xs mb-8">
                      <div className="w-1/2 pr-4 space-y-1">
                        <div><b>Total Value (In Words):</b></div>
                        <div className="text-indigo-900 font-semibold italic">{numberToWords(Number(selectedCN.Total_Amount))} Only</div>
                      </div>
                      <div className="w-1/2 pl-4 border-l border-slate-100 space-y-2">
                        <div className="flex justify-between text-slate-500">
                          <span>Taxable Value:</span>
                          <span className="font-semibold text-slate-800">₹ {Number(taxableValue).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        {Number(selectedCN.CGSTAmount) > 0 && (
                          <div className="flex justify-between text-slate-500">
                            <span>CGST Offset:</span>
                            <span className="font-semibold text-slate-800">₹ {Number(selectedCN.CGSTAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {Number(selectedCN.SGSTAmount) > 0 && (
                          <div className="flex justify-between text-slate-500">
                            <span>SGST Offset:</span>
                            <span className="font-semibold text-slate-800">₹ {Number(selectedCN.SGSTAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {Number(selectedCN.IGSTAmount) > 0 && (
                          <div className="flex justify-between text-slate-500">
                            <span>IGST Offset:</span>
                            <span className="font-semibold text-slate-800">₹ {Number(selectedCN.IGSTAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm font-extrabold text-slate-900 border-t pt-2 border-slate-100">
                          <span>Grand Total Offset:</span>
                          <span className="text-indigo-700 text-base">₹ {Number(selectedCN.Total_Amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>

                    {/* Signature block */}
                    <div className="flex justify-between items-end border-t border-slate-100 pt-6 mt-12 text-xs">
                      <div>
                        <div className="text-slate-400">Customer Seal / Signature</div>
                        <div className="border-b w-32 border-dashed border-slate-300 mt-6" />
                      </div>
                      <div className="text-right">
                        <div className="text-slate-400 mb-1">For {activeCompany?.company_name || selectedCN.company_name}</div>
                        {activeCompany?.signature ? (
                          <img 
                            src={`${API_URL}${activeCompany.signature}`} 
                            alt="Signature" 
                            className="h-10 inline-block mb-1 object-contain"
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <div className="h-10" />
                        )}
                        <div className="font-bold text-slate-700">Authorized Signatory</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50/50 rounded-3xl border-2 border-dashed border-slate-200 p-12 text-center text-slate-400">
                Select a Credit Note from the list on the left to display its full document preview and download options.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditNotesList;
